/**
 * VariableBindingsSection Component
 *
 * Destination section for binding URL params or server data → V2 variables.
 * These bindings are applied at runtime BEFORE the first screen renders,
 * allowing conditions and @variable text to use externally-sourced values.
 *
 * @module webplacements/components/editors/destinations
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LinkIcon from '@mui/icons-material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { FunnelVariable } from '@segmently/schema-models/v2';
import type {
  VariableBinding,
  ParameterDefinition,
} from '@modules/webplacements/types/webplacements.types';

// ============================================================================
// TYPES
// ============================================================================

export interface VariableBindingsSectionProps {
  /** Current variable bindings */
  bindings: VariableBinding[];
  /** Called when bindings change */
  onChange: (bindings: VariableBinding[]) => void;
  /** Available V2 variables for target dropdown */
  availableVariables: FunnelVariable[];
  /** Defined URL params (for source key autocomplete) */
  urlParams: ParameterDefinition[];
  /** Defined server data (for source key autocomplete) */
  serverData: ParameterDefinition[];
  /** Whether the editor is disabled */
  disabled?: boolean;
}

type SourceType = 'url' | 'server';

interface NewBindingState {
  sourceType: SourceType;
  sourceKey: string;
  variableId: string;
}

const initialNewBinding: NewBindingState = {
  sourceType: 'url',
  sourceKey: '',
  variableId: '',
};

/** Variable types compatible with external data binding (URL params, server data).
 * enum/enum[] excluded — they require internal option UUIDs which external sources don't know. */
const BINDABLE_VARIABLE_TYPES = new Set(['string', 'number', 'boolean', 'date']);

// ============================================================================
// COMPONENT
// ============================================================================

export const VariableBindingsSection: React.FC<VariableBindingsSectionProps> = ({
  bindings,
  onChange,
  availableVariables,
  urlParams,
  serverData,
  disabled = false,
}) => {
  const theme = useTheme();
  const [isAdding, setIsAdding] = useState(false);
  const [newBinding, setNewBinding] = useState<NewBindingState>(initialNewBinding);

  // Variables already bound — excluded from target dropdown
  const boundVariableIds = new Set(bindings.map((b) => b.variableId));
  const unboundVariables = availableVariables.filter(
    (v) => !boundVariableIds.has(v.id) && BINDABLE_VARIABLE_TYPES.has(v.type)
  );

  // Source key suggestions based on source type
  const sourceKeySuggestions = newBinding.sourceType === 'url'
    ? urlParams.map((p) => p.sourceDetails.urlParam || p.key)
    : serverData.map((p) => p.sourceDetails.serverPath || p.key);

  // --- Handlers ---

  const handleAddClick = useCallback(() => {
    setIsAdding(true);
    setNewBinding(initialNewBinding);
  }, []);

  const handleCancelAdd = useCallback(() => {
    setIsAdding(false);
    setNewBinding(initialNewBinding);
  }, []);

  const handleSaveNew = useCallback(() => {
    if (!newBinding.sourceKey.trim() || !newBinding.variableId) return;

    const targetVar = availableVariables.find((v) => v.id === newBinding.variableId);
    if (!targetVar) return;

    const binding: VariableBinding = {
      sourceType: newBinding.sourceType,
      sourceKey: newBinding.sourceKey.trim(),
      variableId: targetVar.id,
      variableName: targetVar.name,
      variableType: targetVar.type,
    };

    onChange([...bindings, binding]);
    setIsAdding(false);
    setNewBinding(initialNewBinding);
  }, [newBinding, availableVariables, bindings, onChange]);

  const handleDelete = useCallback(
    (index: number) => {
      onChange(bindings.filter((_, i) => i !== index));
    },
    [bindings, onChange]
  );

  // --- Render ---

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <LinkIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
        <Typography variant="body2" color="text.secondary">
          Map external data sources to V2 variables at funnel start
        </Typography>
        <Tooltip title="Variable bindings populate V2 variables from URL parameters or server data BEFORE the first screen renders. This enables conditions and @variable text to use these values immediately.">
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {!isAdding && (
          <Button
            data-testid="wp-var-binding-add-btn"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            disabled={disabled || unboundVariables.length === 0}
          >
            Add Binding
          </Button>
        )}
      </Box>

      {/* Bindings Table */}
      {bindings.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '20%' }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '30%' }}>Parameter</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '5%' }} />
                <TableCell sx={{ fontWeight: 600, width: '30%' }}>Variable</TableCell>
                <TableCell align="right" sx={{ width: '15%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bindings.map((binding, index) => (
                <TableRow key={`${binding.sourceType}-${binding.sourceKey}-${binding.variableId}`} hover data-testid={`wp-var-binding-row-${index}`}>
                  <TableCell>
                    <Chip
                      label={binding.sourceType === 'url' ? 'URL' : 'Server'}
                      size="small"
                      color={binding.sourceType === 'url' ? 'primary' : 'warning'}
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={binding.sourceKey}
                      size="small"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <ArrowForwardIcon fontSize="small" color="action" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {binding.variableName || binding.variableId}
                      </Typography>
                      {binding.variableType && (
                        <Chip
                          label={binding.variableType}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      data-testid={`wp-var-binding-delete-${index}`}
                      size="small"
                      onClick={() => handleDelete(index)}
                      disabled={disabled}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add New Binding Form */}
      {isAdding && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Source Type */}
          <FormControl size="small" fullWidth>
            <InputLabel>Source Type</InputLabel>
            <Select
              data-testid="wp-var-binding-source-type"
              value={newBinding.sourceType}
              onChange={(e) =>
                setNewBinding((prev) => ({
                  ...prev,
                  sourceType: e.target.value as SourceType,
                  sourceKey: '', // Reset key when type changes
                }))
              }
              label="Source Type"
            >
              <MenuItem value="url">URL Parameter</MenuItem>
              <MenuItem value="server">Server Data</MenuItem>
            </Select>
          </FormControl>

          {/* Source Key with autocomplete */}
          <Autocomplete
            freeSolo
            options={sourceKeySuggestions}
            value={newBinding.sourceKey}
            onInputChange={(_, value) =>
              setNewBinding((prev) => ({ ...prev, sourceKey: value }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={newBinding.sourceType === 'url' ? 'URL Parameter Name' : 'Server Data Path'}
                size="small"
                fullWidth
                required
                placeholder={newBinding.sourceType === 'url' ? 'utm_source' : 'geoData.country'}
                helperText={
                  newBinding.sourceType === 'url'
                    ? 'Name of the URL query parameter (e.g. ?utm_source=google)'
                    : 'Dot-path in SERVER_DATA (e.g. geoData.country)'
                }
              />
            )}
          />

          {/* Target Variable */}
          <FormControl size="small" fullWidth required>
            <InputLabel>Target Variable</InputLabel>
            <Select
              data-testid="wp-var-binding-target-var"
              value={newBinding.variableId}
              onChange={(e) =>
                setNewBinding((prev) => ({ ...prev, variableId: e.target.value }))
              }
              label="Target Variable"
            >
              {unboundVariables.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{v.name}</Typography>
                    <Chip
                      label={v.type}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem' }}
                    />
                  </Box>
                </MenuItem>
              ))}
              {unboundVariables.length === 0 && (
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    {availableVariables.some((v) => !boundVariableIds.has(v.id))
                      ? 'Remaining variables are enum type — not bindable to external data'
                      : 'All variables are already bound'}
                  </Typography>
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button data-testid="wp-var-binding-cancel-btn" size="small" onClick={handleCancelAdd}>
              Cancel
            </Button>
            <Button
              data-testid="wp-var-binding-save-btn"
              size="small"
              variant="contained"
              onClick={handleSaveNew}
              disabled={!newBinding.sourceKey.trim() || !newBinding.variableId}
            >
              Add
            </Button>
          </Box>
        </Box>
      )}

      {/* Empty State */}
      {bindings.length === 0 && !isAdding && (
        <Box
          sx={{
            p: 2,
            textAlign: 'center',
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No variable bindings configured.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {availableVariables.length === 0
              ? 'Create V2 variables first, then bind them to URL params or server data.'
              : unboundVariables.length === 0 && availableVariables.length > 0
                ? 'Only string, number, boolean, and date variables can be bound to external data. Enum variables receive values from screen interactions.'
                : 'Bind URL params or server data to variables for conditions and @variable text.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default VariableBindingsSection;
